import { onMounted, onUnmounted, watchEffect, WatchStopHandle } from 'vue'
import { useUserStore } from '@/store/user'

/**
 * Composable that patches the hardcoded "Page N" tooltip on Kendo pager numeric buttons
 * with a locale-appropriate equivalent (e.g. "Side N" in Danish, "Seite N" in German).
 *
 * Kendo's PagerNumericButtons renders `title="Page N"` unconditionally in its source —
 * there is no message key for it — so we intercept via MutationObserver.
 */
export function useLocalizedPagerTitles() {
    const userStore = useUserStore()
    let observer: MutationObserver | null = null
    let stopWatcher: WatchStopHandle | null = null

    function getPageWord(): string {
        switch (userStore.language) {
            case 'K': return 'Side'
            case 'D': return 'Seite'
            case 'V': return 'Sida'
            default:  return 'Page'
        }
    }

    function patchPagerTitles() {
        const word = getPageWord()
        document.querySelectorAll<Element>('[title]').forEach(el => {
            const title = el.getAttribute('title')!
            const match = title.match(/^(?:Page|Side|Seite|Sida) (\d+)$/)
            if (match) {
                const newTitle = `${word} ${match[1]}`
                if (title !== newTitle) {
                    el.setAttribute('title', newTitle)
                }
            }
        })
    }

    onMounted(() => {
        observer = new MutationObserver(patchPagerTitles)
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['title'],
        })
        stopWatcher = watchEffect(patchPagerTitles)
    })

    onUnmounted(() => {
        stopWatcher?.()
        observer?.disconnect()
    })
}
